import { useCallback, useRef } from 'react';
import { ClbButton, ClbCheckbox, ClbIcon, ClbTag } from '@celebration/react';
import { useTranslation } from 'react-i18next';
import { ILoadFilterTags } from './ILoadFilterTags';
import {
    isFilledValue,
    isNonEmptyArray,
    isValidDate,
} from './load-filter-tags.utils';
import { isNotEmpty } from '../../../../utils/string-utils';
import IFiltersModel from '../../../interfaces/IFiltersModel';

export default function LoadFilterTags(props: Readonly<ILoadFilterTags>) {
    const {
        filters,
        selectedCards,
        materialOptions,
        onCleanCardSelection,
        onSelectAllCards,
        cleanCurrentFilters,
        onApplyFilters,
    } = props;
    const { t } = useTranslation();
    const checkBoxRef = useRef<HTMLInputElement>(null);

    const isFiltersFilled = useCallback((): boolean => {
        // eslint-disable-next-line no-restricted-syntax
        for (const key in filters.current) {
            // eslint-disable-next-line no-prototype-builtins
            if (filters.current?.hasOwnProperty(key)) {
                const value = filters.current[key as keyof IFiltersModel];
                if (isFilledValue(value)) {
                    if (
                        isNonEmptyArray(value) ||
                        isValidDate(value) ||
                        isNotEmpty(value)
                    ) {
                        return true;
                    }
                }
            }
        }
        return false;
    }, [filters]);

    const removeFilterTag = useCallback(
        (uniqueKey: string) => {
            const uniqueKeySplit = uniqueKey.split('-');
            const valueIndexToRemove =
                uniqueKeySplit[uniqueKeySplit.length - 1];
            const propertyToRemove = uniqueKeySplit[uniqueKeySplit.length - 2];

            if (
                isNonEmptyArray(
                    filters.current[propertyToRemove as keyof IFiltersModel],
                )
            ) {
                const temporaryArray = filters.current[
                    propertyToRemove as keyof IFiltersModel
                ] as string[];

                filters.current[propertyToRemove as keyof IFiltersModel] =
                    temporaryArray.filter(
                        item => item !== valueIndexToRemove,
                    ) as any;
            } else {
                filters.current[propertyToRemove as keyof IFiltersModel] =
                    undefined;
            }

            onApplyFilters(filters.current);
        },
        [filters, onApplyFilters],
    );

    const getFilterTag = useCallback(
        (uniqueKey: string, tagValue: string) => {
            return (
                <ClbTag
                    id={uniqueKey}
                    key={uniqueKey}
                    type="filter"
                    onClickRemove={() => removeFilterTag(uniqueKey)}
                >
                    {tagValue}
                </ClbTag>
            );
        },
        [removeFilterTag],
    );

    const handleTagsFromArray = useCallback(
        (key: string, value: string[], tags: JSX.Element[]) => {
            value.forEach(e => {
                const tagValue =
                    t(`LoadFilterTagsPrefix.${key}`) +
                    t(
                        `LoadFilterTagsValues.${e}`,
                        key === 'product' && materialOptions !== undefined
                            ? `${materialOptions.find(m => m.value === e)?.label}`
                            : e,
                    );
                const uniqueKey = `filter-tag-${key}-${e}`;

                tags.push(getFilterTag(uniqueKey, tagValue));
            });
        },
        [t, materialOptions, getFilterTag],
    );

    const handleTagFromDate = useCallback(
        (key: string, value: Date, tags: JSX.Element[]) => {
            const date = isValidDate(value)
                ? new Date(value).toLocaleDateString()
                : value?.toString();

            const tagValue = t(`LoadFilterTagsPrefix.${key}`) + date;

            const uniqueKey = `filter-tag-${key}-${date}`;

            if (date.trim().length > 0) {
                tags.push(getFilterTag(uniqueKey, tagValue));
            }
        },
        [t, getFilterTag],
    );

    const renderFilterTags = useCallback((): JSX.Element[] => {
        let tags: JSX.Element[] = [];

        Object.keys(filters.current).forEach(key => {
            const value = filters.current[key as keyof IFiltersModel];

            if (isFilledValue(value)) {
                if (isNonEmptyArray(value)) {
                    handleTagsFromArray(key, value as string[], tags);
                } else {
                    handleTagFromDate(key, value as Date, tags);
                }
            }
        });

        if (tags.length > 5) {
            const groupedTag = (
                <ClbTag key="filter-tag-group" type="neutral">
                    +{tags.length - 5}
                </ClbTag>
            );

            tags = tags.slice(0, 5);
            tags = [...tags, groupedTag];
        }

        return tags;
    }, [filters, handleTagsFromArray, handleTagFromDate]);

    const onChangeLoadSelection = (e: any) => {
        const checkBox = e.target.nextSibling as HTMLElement | undefined;
        if (checkBoxRef.current?.checked) {
            checkBox?.classList.add('checkmark--checked');
            onSelectAllCards!();
        } else {
            checkBox?.classList.remove('checkmark--checked');
            onCleanCardSelection!();
        }
    };

    const getSelectionInfo = () => {
        const numberOfselectedCards = selectedCards?.length ?? 0;
        return `${numberOfselectedCards} ${
            numberOfselectedCards > 1
                ? t('Loads.CargasSelecionadas')
                : t('Loads.CargaSelecionada')
        }`;
    };

    return (
        <div id="loads-filter-tags" className="loads-filter-tags">
            <div
                className="loads-filter-tags__left-side"
                data-testid="loads-filter-tags__left-side"
            >
                {isFiltersFilled() && (
                    <span>
                        {t('FilteredBy')} {renderFilterTags()}
                    </span>
                )}
            </div>
            <div className="loads-filter-tags__right-side">
                {selectedCards && selectedCards.length >= 1 && (
                    <>
                        <ClbCheckbox
                            ref={checkBoxRef}
                            onChange={(e: any) => onChangeLoadSelection(e)}
                            data-testid="loads-filter-selection-checkbox"
                        >
                            {t('Loads.SelecionarTodas')}
                        </ClbCheckbox>
                        <span className="loads-filter-tags__selection-counter">
                            <ClbIcon icon="check" />
                            <span data-testid="loads-filter-tags-selection-counter">
                                {getSelectionInfo()}
                            </span>
                        </span>
                    </>
                )}
                {((selectedCards && selectedCards.length >= 1) ||
                    isFiltersFilled()) && (
                    <ClbButton
                        id="loads-filter-tags-clean-filters"
                        data-testid="loads-filter-tags-clean-filters"
                        label={t('LimparFiltros')}
                        type="button"
                        blockedWidth
                        styleType="tertiary"
                        size="sm"
                        onClick={cleanCurrentFilters}
                        icon="cancel"
                    />
                )}
            </div>
        </div>
    );
}
